import { motion, type MotionStyle } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { attachCardMotion } from '../lib/cardMotion';
import { CARD, topFor, unit } from '../lib/cardLayout';
import { qrMatrix } from '../lib/qr';
import { LEVEL_IDENTITY } from '../lib/emblem';
import type { LevelId, ResultTypeId, UiCopy } from '../lib/types';

export interface CollectibleCardProps {
  ui: UiCopy;
  typeId: ResultTypeId;
  score: number;
  percentile: number;
  levelId: LevelId;
  /** `full` may include the assessment role; shared URLs omit it. */
  variant: 'full' | 'shared';
  roleLabel?: string;
  /** Encoded into the card's scan target. */
  siteUrl: string;
  interactive?: boolean;
}

/**
 * The single card object. The result screen, the shared-result destination and
 * the PNG export all render from this component or from its geometry and
 * layout table, so the three surfaces stay one design.
 */
export function CollectibleCard({
  ui,
  typeId,
  score,
  percentile,
  levelId,
  variant,
  roleLabel,
  siteUrl,
  interactive = true,
}: CollectibleCardProps) {
  const cardRef = useRef<HTMLElement | null>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const levelIdentity = LEVEL_IDENTITY[levelId];
  const type = ui.types[typeId];
  const level = ui.levels[levelId];

  useEffect(() => {
    const node = cardRef.current;
    if (!node || !interactive) return;
    return attachCardMotion(node);
  }, [interactive, typeId, score]);

  useEffect(() => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const qr = qrMatrix(siteUrl);
    const size = 256;
    const quiet = 14;
    const moduleSize = (size - quiet * 2) / qr.size;
    canvas.width = size;
    canvas.height = size;
    context.imageSmoothingEnabled = false;
    context.fillStyle = '#f4f1ea';
    context.fillRect(0, 0, size, size);
    context.fillStyle = '#0a0b0f';
    for (let y = 0; y < qr.size; y += 1) {
      for (let x = 0; x < qr.size; x += 1) {
        if (!qr.modules[y]?.[x]) continue;
        context.fillRect(
          Math.floor(quiet + x * moduleSize),
          Math.floor(quiet + y * moduleSize),
          Math.ceil(moduleSize),
          Math.ceil(moduleSize),
        );
      }
    }
  }, [siteUrl]);

  const levelWord = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'][levelIdentity.number];
  const identityMeta = [variant === 'full' ? roleLabel : '', level.title].filter(Boolean).join(' · ');
  const testTypeLabel = ui.share.testTypeLabel.trim().toUpperCase() === 'TEST TYPE'
    ? 'TEST TYPE'
    : `${ui.share.testTypeLabel} / TEST TYPE`;

  return (
    <motion.article
      ref={cardRef}
      className={`tl-card tl-card-${typeId}${interactive ? ' is-interactive' : ''}`}
      data-variant={variant}
      style={{
        '--card-accent': levelIdentity.accent,
        '--card-accent-rgb': levelIdentity.accentRgb,
        '--card-glow': levelIdentity.glow,
      } as MotionStyle}
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
      aria-label={type.title}
    >
      <div className="tl-card-plate">
        <img className="tl-card-generated" src={levelIdentity.artwork} alt="" aria-hidden="true" />
        <span className="tl-card-gloss" aria-hidden="true" />

        <header className="tl-card-rail" style={{ top: topFor(CARD.rail.y, CARD.rail.size), fontSize: unit(CARD.rail.size) }}>
          <span aria-hidden="true">—</span>
          <strong>{ui.share.boundaryTitle}</strong>
          <span aria-hidden="true">—</span>
        </header>

        <section className="tl-card-identity" style={{ top: topFor(CARD.identity.labelY, CARD.identity.labelSize) }}>
          <span style={{ fontSize: unit(CARD.identity.labelSize) }}>{ui.share.roleLabel} / IDENTITY</span>
          <strong
            style={{
              fontSize: unit(CARD.identity.titleSize),
              marginTop: unit(CARD.identity.titleY - CARD.identity.labelY - CARD.identity.titleSize * 0.8),
            }}
          >
            {type.title}
          </strong>
          <em
            style={{
              fontSize: unit(CARD.identity.metaSize),
              marginTop: unit(CARD.identity.metaY - CARD.identity.titleY - CARD.identity.metaSize * 0.8),
            }}
          >
            {identityMeta}
          </em>
        </section>

        <section className="tl-card-level" style={{ top: topFor(CARD.level.y, CARD.level.numberSize) }}>
          <div>
            <span style={{ fontSize: unit(CARD.level.labelSize) }}>{ui.share.levelLabel}</span>
            <b style={{ fontSize: unit(CARD.level.numberSize) }}>{levelIdentity.number}</b>
          </div>
          <em
            style={{
              fontSize: unit(CARD.level.englishSize),
              marginTop: unit(CARD.level.englishY - CARD.level.y - CARD.level.englishSize * 0.8),
            }}
          >
            LEVEL {levelWord}
          </em>
        </section>

        <p
          className="tl-card-description"
          style={{ top: topFor(CARD.desc.y, CARD.desc.size), fontSize: unit(CARD.desc.size), lineHeight: unit(CARD.desc.leading) }}
        >
          {level.description}
        </p>

        <div className="tl-card-score" style={{ top: topFor(CARD.score.y, CARD.score.size) }}>
          <b style={{ fontSize: unit(CARD.score.size) }}>{score}</b>
          <span style={{ fontSize: unit(CARD.score.unitSize) }}>/100</span>
        </div>
        <div className="tl-card-meta" style={{ top: topFor(CARD.meta.labelY, CARD.meta.labelSize) }}>
          <dl>
            <dt style={{ fontSize: unit(CARD.meta.labelSize) }}>{testTypeLabel}</dt>
            <dd
              style={{
                fontSize: unit(CARD.meta.valueSize),
                marginTop: unit(CARD.meta.valueY - CARD.meta.labelY - CARD.meta.valueSize * 0.8),
              }}
            >
              {ui.share.cardTitle}
            </dd>
          </dl>
          <dl style={{ left: unit(CARD.meta.refX) }}>
            <dt style={{ fontSize: unit(CARD.meta.labelSize) }}>{ui.share.referenceLabel} / REF</dt>
            <dd
              style={{
                fontSize: unit(CARD.meta.valueSize),
                marginTop: unit(CARD.meta.valueY - CARD.meta.labelY - CARD.meta.valueSize * 0.8),
              }}
            >
              REF·P{percentile}
            </dd>
          </dl>
        </div>

        <canvas
          ref={qrCanvasRef}
          className="tl-card-qr"
          style={{ top: unit(CARD.qr.y), width: unit(CARD.qr.size), height: unit(CARD.qr.size) }}
          role="img"
          aria-label={siteUrl}
        />

      </div>
    </motion.article>
  );
}
